import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { PortalOrder, PortalOrderLine } from '@provalia/shared';
import { Badge, Card, Confirm, ErrorBox, Loading, errorText, useAsync, useToast } from '../../shared/ui';
import { fmtDate, fmtMoney, fmtNumber, relDays } from '../../shared/format';
import { api, type OrderDetail } from '../api';
import { canReceive, isBuyer, isSupplier, usePortal } from '../context';
import {
  AcceptanceBadge,
  DateStatusBadge,
  DrawingCell,
  GreenLightBadge,
  PriceStatusBadge,
  PriceThumb,
  ReceiptBadge,
  UrgencyBadge,
  lineLabel,
  requiredOf,
} from '../components/status';
import { DatesModal, DeliveryNoteModal, RejectModal, ReviewModal } from '../components/OrderModals';
import { ActivityCard, DeliveryNotesCard } from '../components/OrderHistory';

type ModalState =
  | { kind: 'accept' }
  | { kind: 'reject' }
  | { kind: 'dates' }
  | { kind: 'note' }
  | { kind: 'review'; line: PortalOrderLine; what: 'date' | 'price'; decision: 'approve' | 'reject' };

export function Pedido(): JSX.Element {
  const { docEntry = '' } = useParams();
  const { me } = usePortal();
  const role = me.session.role;
  const { data, error, reload } = useAsync(() => api.get<OrderDetail>(`/portal/orders/${encodeURIComponent(docEntry)}`), [docEntry]);
  const [modal, setModal] = useState<ModalState | null>(null);
  const toast = useToast();

  if (error) {
    return (
      <div className="stack lg">
        <Crumbs label={`Pedido ${docEntry}`} />
        <ErrorBox error={error} onRetry={() => void reload()} />
        <div>
          <Link className="btn" to="/">
            ← Volver a la cartera
          </Link>
        </div>
      </div>
    );
  }
  if (!data) return <Loading label="Leyendo el pedido de SAP…" />;

  const { order, activity, deliveryNotes } = data;
  const supplier = isSupplier(role);
  const refresh = (): void => void reload();
  const currency = order.lines[0]?.currency ?? 'EUR';

  return (
    <div className="stack lg">
      <div className="page-head">
        <div>
          <Crumbs label={`Pedido ${order.docNum}`} />
          <h1>
            Pedido {order.docNum}
            {supplier ? null : <span className="pt-h1-sub"> · {order.cardName}</span>}
          </h1>
          <div className="row" style={{ marginTop: 6 }}>
            <AcceptanceBadge status={order.acceptance} />
            <UrgencyBadge urgency={order.urgency} />
            {order.changes.length > 0 ? <Badge tone="info">Modificado</Badge> : null}
          </div>
        </div>
        <button className="btn" onClick={refresh}>
          Recargar
        </button>
      </div>

      {supplier ? (
        <>
          {me.viewAs ? (
            <div className="callout info">
              Estás actuando en nombre de <strong>{me.viewAs.supplierName}</strong>. Lo que hagas queda registrado a tu nombre ({me.session.email}),
              no al del proveedor.
            </div>
          ) : null}
          <SupplierActions order={order} tenantName={me.tenant.name} onAction={setModal} />
        </>
      ) : (
        <StaffNotices order={order} buyer={isBuyer(role)} />
      )}

      <Card title="Datos del pedido">
        <dl className="kv">
          <dt>Proveedor</dt>
          <dd>
            {order.cardName} <span className="subtle">({order.cardCode})</span>
          </dd>
          <dt>Fecha del pedido</dt>
          <dd>{fmtDate(order.docDate)}</dd>
          <dt>Enviado al portal</dt>
          <dd>{fmtDate(order.sentAt)}</dd>
          <dt>{order.acceptance === 'R' ? 'Rechazado' : 'Aceptado'}</dt>
          <dd>
            {order.acceptance === 'P'
              ? 'Todavía no'
              : `${fmtDate(order.acceptedAt)}${order.acceptedBy ? ` · ${order.acceptedBy}` : ''}`}
          </dd>
          {order.acceptance === 'R' && order.rejectReason ? (
            <>
              <dt>Motivo del rechazo</dt>
              <dd>{order.rejectReason}</dd>
            </>
          ) : null}
          {order.comments ? (
            <>
              <dt>Comentarios</dt>
              <dd className="pt-wrap">{order.comments}</dd>
            </>
          ) : null}
          <dt>Tolerancias</dt>
          <dd>
            Fecha ±{me.rules.dateToleranceDays} días · precio ±{fmtNumber(me.rules.priceTolerancePct, 0)} %
          </dd>
          {order.hasAttachments ? (
            <>
              <dt>Anexos</dt>
              <dd>El pedido tiene documentos anexos en SAP</dd>
            </>
          ) : null}
        </dl>
      </Card>

      <LinesCard order={order} onReview={setModal} onChanged={refresh} />

      <div className="grid cols-2 pt-history">
        <ActivityCard activity={activity} tenantName={me.tenant.name} />
        <DeliveryNotesCard notes={deliveryNotes} currency={currency} />
      </div>

      {modal?.kind === 'accept' ? (
        <Confirm
          title={`Aceptar el pedido ${order.docNum}`}
          message={
            <div className="stack sm">
              <p>
                Confirmas a {me.tenant.name} que has recibido el pedido y lo asumes. Se grabará en SAP con tu usuario y la fecha de hoy.
              </p>
              <p className="muted">Después podrás proponer las fechas de entrega de cada línea.</p>
            </div>
          }
          confirmLabel="Aceptar pedido"
          onConfirm={async () => {
            await api.post(`/portal/orders/${order.docEntry}/accept`);
            toast(`Pedido ${order.docNum} aceptado`, 'ok');
            await reload();
            // Apartado 6.2: tras aceptar, se le ofrecen las fechas en la misma pantalla.
            setModal({ kind: 'dates' });
          }}
          onClose={() => setModal((m) => (m?.kind === 'accept' ? null : m))}
        />
      ) : null}
      {modal?.kind === 'reject' ? <RejectModal order={order} onClose={() => setModal(null)} onDone={refresh} /> : null}
      {modal?.kind === 'dates' ? (
        <DatesModal order={order} toleranceDays={me.rules.dateToleranceDays} onClose={() => setModal(null)} onDone={refresh} />
      ) : null}
      {modal?.kind === 'note' ? (
        <DeliveryNoteModal order={order} tolerancePct={me.rules.priceTolerancePct} onClose={() => setModal(null)} onDone={refresh} />
      ) : null}
      {modal?.kind === 'review' ? (
        <ReviewModal
          order={order}
          line={modal.line}
          kind={modal.what}
          decision={modal.decision}
          rules={me.rules}
          onClose={() => setModal(null)}
          onDone={refresh}
        />
      ) : null}
    </div>
  );
}

function Crumbs({ label }: { label: string }): JSX.Element {
  return (
    <div className="crumbs">
      <Link to="/">Cartera</Link> / {label}
    </div>
  );
}

// ── Lo que tiene que hacer el proveedor, según el estado ──────────────────

function SupplierActions({
  order,
  tenantName,
  onAction,
}: {
  order: PortalOrder;
  tenantName: string;
  onAction: (m: ModalState) => void;
}): JSX.Element {
  const open = order.lines.filter((l) => l.open);
  const changes =
    order.changes.length > 0 ? (
      <div className="callout warn">
        <div className="title">El pedido ha cambiado desde que lo aceptaste</div>
        <ul className="pt-list">
          {order.changes.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
      </div>
    ) : null;

  if (order.acceptance === 'P') {
    return (
      <div className="stack">
        {changes}
        <div className="callout warn pt-banner pt-action">
          <div>
            <div className="title pt-action-title">Tienes que aceptar este pedido</div>
            <div>
              Revisa las líneas de abajo: artículo, cantidad, precio, fecha requerida y versión de plano. Al aceptarlo podrás proponer las fechas de
              entrega y comunicar el albarán.
            </div>
          </div>
          <div className="row">
            <button className="btn primary pt-big" onClick={() => onAction({ kind: 'accept' })}>
              Aceptar pedido
            </button>
            <button className="btn danger pt-big" onClick={() => onAction({ kind: 'reject' })}>
              Rechazar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (order.acceptance === 'R') {
    return (
      <div className="callout bad">
        <div className="title">Has rechazado este pedido</div>
        {order.rejectReason ? <div>Motivo: {order.rejectReason}</div> : null}
        <div className="muted">Si ha sido un error o {tenantName} lo modifica, volverá a aparecer como pendiente de aceptar.</div>
      </div>
    );
  }

  const withoutDate = open.filter((l) => l.dateStatus === 'P' || l.dateStatus === 'R').length;
  const inReview = open.filter((l) => l.dateStatus === 'V' || l.priceStatus === 'V').length;
  return (
    <div className="stack">
      {changes}
      <div className={`callout ${withoutDate ? 'info' : 'ok'} pt-banner`}>
        <div>
          <div className="title">
            {open.length === 0
              ? 'Pedido aceptado y sin líneas abiertas'
              : withoutDate
                ? `Pedido aceptado: confirma las fechas de entrega (${withoutDate} ${withoutDate === 1 ? 'línea' : 'líneas'} sin fecha)`
                : 'Pedido aceptado'}
          </div>
          <div>
            {inReview
              ? `${inReview} ${inReview === 1 ? 'línea está' : 'líneas están'} en revisión de compras.`
              : 'Cuando envíes la mercancía, comunica el albarán con sus precios.'}
          </div>
        </div>
        {open.length > 0 ? (
          <div className="row">
            <button className={`btn ${withoutDate ? 'primary' : ''} pt-big`} onClick={() => onAction({ kind: 'dates' })}>
              Proponer fechas
            </button>
            <button className={`btn ${withoutDate ? '' : 'primary'} pt-big`} onClick={() => onAction({ kind: 'note' })}>
              Comunicar albarán
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StaffNotices({ order, buyer }: { order: PortalOrder; buyer: boolean }): JSX.Element | null {
  const pending = order.lines.filter((l) => l.open && (l.dateStatus === 'V' || l.priceStatus === 'V')).length;
  return (
    <>
      {order.changes.length > 0 ? (
        <div className="callout warn">
          <div className="title">El pedido ha cambiado desde que el proveedor lo aceptó</div>
          <ul className="pt-list">
            {order.changes.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
          <div className="muted">Está pendiente de que el proveedor lo vuelva a aceptar.</div>
        </div>
      ) : null}
      {order.acceptance === 'P' && order.changes.length === 0 ? (
        <div className="callout warn">
          <div className="title">Pendiente de que el proveedor lo acepte</div>
          <div className="muted">
            {order.sentAt ? `Enviado al portal el ${fmtDate(order.sentAt)} (${relDays(order.sentAt)}).` : 'Aún no se ha publicado en el portal.'}
          </div>
        </div>
      ) : null}
      {order.acceptance === 'R' ? (
        <div className="callout bad">
          <div className="title">El proveedor ha rechazado este pedido</div>
          {order.rejectReason ? <div>Motivo: {order.rejectReason}</div> : null}
          {buyer ? (
            <div className="muted">Modifícalo en SAP y el portal se lo volverá a enviar, o gestiónalo con otro proveedor.</div>
          ) : null}
        </div>
      ) : null}
      {buyer && pending > 0 ? (
        <div className="callout warn">
          <div className="title">
            {pending === 1 ? '1 línea tiene' : `${pending} líneas tienen`} fecha o precio fuera de tolerancia pendiente de validar
          </div>
          <div className="muted">Usa los botones «Aprobar» o «Rechazar» de cada línea, en la tabla de abajo.</div>
        </div>
      ) : null}
    </>
  );
}

// ── Líneas, validaciones y recepción ──────────────────────────────────────

function LinesCard({
  order,
  onReview,
  onChanged,
}: {
  order: PortalOrder;
  onReview: (m: ModalState) => void;
  onChanged: () => void;
}): JSX.Element {
  const { me } = usePortal();
  const role = me.session.role;
  const buyer = isBuyer(role);
  const receiver = canReceive(role);
  const toast = useToast();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<'confirm' | 'undo' | null>(null);

  const openLines = order.lines.filter((l) => l.open);
  const selectedLines = openLines.filter((l) => selected.has(l.lineNum));
  const toConfirm = selectedLines.filter((l) => !l.physicalReceipt).map((l) => l.lineNum);
  const toUndo = selectedLines.filter((l) => l.physicalReceipt).map((l) => l.lineNum);
  const allSelected = openLines.length > 0 && selectedLines.length === openLines.length;

  function toggle(lineNum: number): void {
    const next = new Set(selected);
    if (next.has(lineNum)) next.delete(lineNum);
    else next.add(lineNum);
    setSelected(next);
  }

  async function receipt(undo: boolean): Promise<void> {
    const lines = undo ? toUndo : toConfirm;
    setBusy(undo ? 'undo' : 'confirm');
    try {
      await api.post(`/portal/orders/${order.docEntry}/receipt`, { lines, undo });
      toast(
        undo
          ? `Recepción deshecha en ${lines.length} ${lines.length === 1 ? 'línea' : 'líneas'}`
          : `Recepción física confirmada en ${lines.length} ${lines.length === 1 ? 'línea' : 'líneas'}`,
        'ok',
      );
      setSelected(new Set());
      onChanged();
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2>Líneas del pedido</h2>
        <span className="subtle">
          {openLines.length} de {order.lines.length} abiertas
        </span>
      </div>
      {receiver ? (
        <div className="pt-receipt-bar">
          <p className="subtle">
            La luz verde para la entrada de mercancía necesita las dos validaciones: el precio validado (automático o por compras) y la recepción física
            en playa, que se confirma aquí marcando las líneas.
            {order.acceptance !== 'A' ? ' Ojo: el proveedor todavía no ha aceptado este pedido.' : ''}
          </p>
          <div className="row">
            <button className="btn primary" disabled={toConfirm.length === 0 || busy !== null} onClick={() => void receipt(false)}>
              {busy === 'confirm' ? 'Guardando…' : `Confirmar recepción física en playa${toConfirm.length ? ` (${toConfirm.length})` : ''}`}
            </button>
            <button className="btn" disabled={toUndo.length === 0 || busy !== null} onClick={() => void receipt(true)}>
              {busy === 'undo' ? 'Guardando…' : `Deshacer recepción${toUndo.length ? ` (${toUndo.length})` : ''}`}
            </button>
          </div>
        </div>
      ) : null}
      <div className="table-wrap">
        <table className="table pt-lines">
          <thead>
            <tr>
              {receiver ? (
                <th>
                  <input
                    type="checkbox"
                    className="pt-checkbox"
                    aria-label="Marcar todas las líneas abiertas"
                    checked={allSelected}
                    disabled={openLines.length === 0}
                    onChange={() => setSelected(allSelected ? new Set() : new Set(openLines.map((l) => l.lineNum)))}
                  />
                </th>
              ) : null}
              <th>Línea</th>
              <th>Artículo</th>
              <th>Plano</th>
              <th className="num">Cantidad</th>
              <th>Requerida</th>
              <th>Propuesta</th>
              <th>Comprometida</th>
              <th className="num">Precio</th>
              <th>Albarán</th>
              <th>Recepción física</th>
              <th>Luz verde</th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((l) => (
              <tr key={l.lineNum} className={l.open ? '' : 'urg-done'}>
                {receiver ? (
                  <td>
                    <input
                      type="checkbox"
                      className="pt-checkbox"
                      aria-label={`Marcar ${lineLabel(l.lineNum).toLowerCase()}`}
                      checked={selected.has(l.lineNum)}
                      disabled={!l.open}
                      onChange={() => toggle(l.lineNum)}
                    />
                  </td>
                ) : null}
                <td className="nowrap">
                  {lineLabel(l.lineNum)}
                  {!l.open ? <div className="subtle">Cerrada</div> : null}
                </td>
                <td>
                  <div className="mono">{l.itemCode}</div>
                  <div className="subtle pt-desc">{l.description}</div>
                </td>
                <td>
                  <DrawingCell docEntry={order.docEntry} lineNum={l.lineNum} version={l.drawingVersion} />
                </td>
                <td className="num nowrap">
                  <strong>{fmtNumber(l.openQuantity)}</strong>
                  <div className="subtle">de {fmtNumber(l.quantity)}</div>
                </td>
                <td className="nowrap">
                  {fmtDate(requiredOf(l))}
                  {l.open ? (
                    <div>
                      <UrgencyBadge urgency={l.urgency} />
                    </div>
                  ) : null}
                </td>
                <td className="nowrap">
                  <div>{fmtDate(l.proposedDate)}</div>
                  <DateStatusBadge status={l.dateStatus} />
                  {buyer && l.open && l.dateStatus === 'V' ? (
                    <div className="row pt-review">
                      <button className="btn ok sm" onClick={() => onReview({ kind: 'review', line: l, what: 'date', decision: 'approve' })}>
                        Aprobar fecha
                      </button>
                      <button className="btn danger sm" onClick={() => onReview({ kind: 'review', line: l, what: 'date', decision: 'reject' })}>
                        Rechazar fecha
                      </button>
                    </div>
                  ) : null}
                </td>
                <td className="nowrap">
                  <strong>{fmtDate(l.confirmedDate)}</strong>
                </td>
                <td className="num nowrap">
                  {fmtMoney(l.unitPrice, l.currency)}
                  {l.originalPrice !== null && Math.abs(l.originalPrice - l.unitPrice) >= 0.005 ? (
                    <div className="subtle">Original {fmtMoney(l.originalPrice, l.currency)}</div>
                  ) : null}
                </td>
                <td className="nowrap">
                  {l.deliveryNoteNumber ? (
                    <div>
                      Nº {l.deliveryNoteNumber} · {fmtMoney(l.deliveryNotePrice, l.currency)}
                    </div>
                  ) : null}
                  <div>
                    <PriceThumb line={l} tolerancePct={me.rules.priceTolerancePct} />
                  </div>
                  <PriceStatusBadge status={l.priceStatus} />
                  {buyer && l.open && l.priceStatus === 'V' ? (
                    <div className="row pt-review">
                      <button className="btn ok sm" onClick={() => onReview({ kind: 'review', line: l, what: 'price', decision: 'approve' })}>
                        Aprobar precio
                      </button>
                      <button className="btn danger sm" onClick={() => onReview({ kind: 'review', line: l, what: 'price', decision: 'reject' })}>
                        Rechazar precio
                      </button>
                    </div>
                  ) : null}
                </td>
                <td className="nowrap">
                  <ReceiptBadge line={l} />
                  {l.physicalReceiptBy ? <div className="subtle">{l.physicalReceiptBy}</div> : null}
                </td>
                <td className="nowrap">
                  <GreenLightBadge on={l.greenLight} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
